import {memo, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Brush,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ReferenceLine
} from 'recharts';
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {getNodeMetrics} from 'src/app/api/dashboard';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {WrapperInput, WrapperInputsGraficas} from './styles';
import ExportCSVChart from './export/Export';


const MetricChartItem = ({nodes, filters, showDates, order, lg}) => {
  const globalMetrics = useSelector((state) => state?.fuse?.dashboard?.dashboard?.metrics);
  const thresHoldsMetrics = useSelector((state) => state?.fuse?.dashboard?.dashboard?.metricThresholdsCharts);
  const [firstRender, setFirstRender] = useState(true);

  const [currentMetrics, setCurrentMetrics] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nodeChartData, setNodeChartData] = useState([]);
  const [threshold, setThreshold] = useState(null);
  const [interfaceChartDataAB, setInterfaceChartDataAB] = useState([]);
  const [interfaceChartDataPAB, setInterfaceChartDataPAB] = useState([]);
  const [interfaceChartDataE, setInterfaceChartDataE] = useState([]);
  const [interfaceChartDataD, setInterfaceChartDataD] = useState([]);
  const [dateInit, setDateInit] = useState(moment().subtract(24, 'hour').format('Y-MM-DD HH:mm:00'));
  const [dateEnd, setDateEnd] = useState(moment().format('Y-MM-DD HH:mm:00'));
  const today = moment();
  const [chartType, setChartType] = useState('line');
  const [chartState, setChartState] = useState({
    data: [],
    top: 'dataMax',
    bottom: 0,
    top2: 'dataMax',
    bottom2: 'dataMin',
    animation: true,
  });
  const [nodo, setNode] = useState('');
  const [interfaz, setInterfaz] = useState('');
  const [graphicInterfaces, setGraphicInterfaces] = useState([]);
  const [metric, setMetric] = useState('');
  const title = metric?.metrica || 'Sin Datos';

  const {data, top, bottom, top2, bottom2} = chartState;

  const [openModal, setOpenModal] = useState(false);

  const styleContainer = {
    height: lg ? '80vh' : '50vh',
    alignItems: 'center',
    width: '100%',
    marginTop: '3rem',
    paddingTop: '3rem'
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Función para calcular el valor máximo para un gráfico
  const getMaxValue = (dataMax, field) => Math.max(...(dataMax?.map((element) => Number(element[field])) || [0])) + 1;

  // useEffect para obtener las métricas relevantes según los datos disponibles
  useEffect(() => {
    if (metric) {

      if (interfaz) {
        let dataInterfaceCurrent = {};
        // eslint-disable-next-line default-case
        switch (metric?.id) {
          case 12 :
            dataInterfaceCurrent = interfaceChartDataAB;
            break;
          case 14:
            dataInterfaceCurrent = interfaceChartDataPAB;
            break;
          case 15:
            dataInterfaceCurrent = interfaceChartDataE;
            break;
          case 16:
            dataInterfaceCurrent = interfaceChartDataD;
            break;
        }

        const addThreshold = Object.keys(dataInterfaceCurrent).reduce((acc, key) => {
          acc[key] = dataInterfaceCurrent[key].map((element) => ({
            ...element,
            'umbral': threshold !== null && threshold !== undefined ? String(threshold) : 0,
          }));
          return acc;
        }, {});

        const topInterfaceValue1 = getMaxValue(dataInterfaceCurrent[interfaz], 'dslectura1');
        const topInterfaceValue2 = getMaxValue(dataInterfaceCurrent[interfaz], 'dslectura2');
        const minDslectura2 = Math.min(...dataInterfaceCurrent[interfaz].map(item => item.dslectura2));
        const minUmbral = Math.min(...dataInterfaceCurrent[interfaz].map(item => item.umbral));
        const bottomCalc = Math.min(minDslectura2, minUmbral) - 5;

        setChartState({
          ...chartState,
          data: addThreshold[interfaz],
          top: topInterfaceValue1,
          top2: topInterfaceValue2,
          bottom2: bottomCalc
        });
      } else if (metric.id in nodeChartData) {
        const topValueNodeCategory = Math.max(...nodeChartData[metric.id].map((element) => Number(element.dslectura1))) + 1;
        const minDslectura2 = Math.min(...nodeChartData[metric.id].map(item => item.dslectura2));
        const minUmbral = Math.min(...nodeChartData[metric.id].map(item => item.umbral));
        const bottomCalc = Math.min(minDslectura2, minUmbral) - 5;

        const addThreshold = Object.keys(nodeChartData).reduce((acc, key) => {
          acc[key] = nodeChartData[key].map((element) => ({
            ...element,
            'umbral': threshold !== null && threshold !== undefined ? String(threshold) : 0,
          }));
          return acc;
        }, {});


        setChartState({
          ...chartState,
          data: addThreshold[metric.id],
          top: topValueNodeCategory,
          bottom2: bottomCalc
        });
      } else {
        setChartState({
          ...chartState,
          data: [],
        });
      }
      setFirstRender(false)
    }
  }, [metric, interfaz]);

  // useEffect para manejar la obtención de datos cuando hay nodos
  useEffect(() => {
    if (nodes.length > 0 && firstRender) {
      setLoading(true);
      fetchChartData(nodes[0].id, order);
    }
  }, [nodes, filters]);

  // useEffect para manejar la obtención de datos cuando hay nodos
  useEffect(() => {
    if (nodo && !firstRender) {
      setLoading(true);
      refreshChartData(nodo, null, null)
    }
  }, [nodo]);

  // Función para obtener los datos del gráfico de acuerdo al nodo seleccionado
  const fetchChartData = async (nodeId, orderCurrent) => {
    const dates = {fechaFin: dateEnd, fechaIni: dateInit};
    const {
      nodeInterfaces,
      metricasGraphicData,
      anchoBandaGraphicData,
      metricsInterfaceData,
      metricsErrorInterfaceData,
      metricsDescInterfaceData,
    } = await getNodeMetrics(nodeId, dates);

    setInterfaceChartDataAB(anchoBandaGraphicData);
    setInterfaceChartDataPAB(metricsInterfaceData);
    setInterfaceChartDataE(metricsErrorInterfaceData);
    setInterfaceChartDataD(metricsDescInterfaceData);
    setGraphicInterfaces(nodeInterfaces);
    setNodeChartData(metricasGraphicData);

    setNode(nodeId);
    setInterfaz('');

    if (orderCurrent !== undefined) {
      let metricsFetched;
      const graphicMetricKeys = Object.keys(metricasGraphicData);
      if (graphicMetricKeys.length > 0) {
        metricsFetched = globalMetrics.filter((globalMetric) => graphicMetricKeys.includes(`${globalMetric.id}`));
      }
      if (nodeInterfaces?.length > 0) {
        metricsFetched = [
          ...metricsFetched,
          {id: 12, metrica: 'Ancho de Banda'},
          {id: 14, metrica: 'Promedio de Ancho de Banda'},
          {id: 15, metrica: 'Errores'},
          {id: 16, metrica: 'Descartes'},
        ];
        setCurrentMetrics(metricsFetched);
        if (orderCurrent === 3) {
          const metricSel = metricsFetched[metricsFetched.length - 4]
          setMetric(metricSel);
          setInterfaz(nodeInterfaces[0].value);
          const selectedThresHold = thresHoldsMetrics?.find(thresholdItem => 
              thresholdItem.id === metricsFetched[metricsFetched.length - 4].id
            ) || 0;
          if (selectedThresHold !== undefined) {
            setThreshold(selectedThresHold.umbralMax);
          }
        } else {
          const metricSel = metricsFetched[orderCurrent]
          setMetric(metricSel);
          const selectedThresHold = thresHoldsMetrics?.find(thresholdItem =>
            thresholdItem.id === metricsFetched?.[orderCurrent]?.id
          ) || 0;
          if (selectedThresHold !== undefined) {
            setThreshold(selectedThresHold.umbralMax)
          }
        }
      } else {
        const metricSel = metricsFetched[orderCurrent]
        setMetric(metricSel);
        setCurrentMetrics(metricsFetched);
      }
    }
    setLoading(false);
  };

  // Función para refrescar los datos del gráfico de acuerdo al nodo seleccionado
  const refreshChartData = async (nodeId, init, end) => {
    setChartState({...chartState, data: []});
    setCurrentMetrics([]);
    setInterfaz('')
    setGraphicInterfaces([]);

    const dates = {fechaIni: init || dateInit, fechaFin: end || dateEnd};
    const {
      nodeInterfaces,
      metricasGraphicData,
      anchoBandaGraphicData,
      metricsInterfaceData,
      metricsErrorInterfaceData,
      metricsDescInterfaceData,
    } = await getNodeMetrics(nodeId, dates);

    setInterfaceChartDataAB(anchoBandaGraphicData);
    setInterfaceChartDataPAB(metricsInterfaceData);
    setInterfaceChartDataE(metricsErrorInterfaceData);
    setInterfaceChartDataD(metricsDescInterfaceData);
    setGraphicInterfaces(nodeInterfaces);
    setNodeChartData(metricasGraphicData);

    let metricsFetched;
    const graphicMetricKeys = Object.keys(metricasGraphicData);
    if (graphicMetricKeys.length > 0) {
      metricsFetched = globalMetrics.filter((globalMetric) => graphicMetricKeys.includes(`${globalMetric.id}`));
    }
    if (nodeInterfaces?.length > 0) {
      metricsFetched = [
        ...metricsFetched,
        {id: 12, metrica: 'Ancho de Banda'},
        {id: 14, metrica: 'Promedio de Ancho de Banda'},
        {id: 15, metrica: 'Errores'},
        {id: 16, metrica: 'Descartes'},
      ];
      setCurrentMetrics(metricsFetched);
    }

    if (interfaz) {
      let dataInterfaceCurrent = {};
      // eslint-disable-next-line default-case
      switch (metric?.id) {
        case 12 :
          dataInterfaceCurrent = interfaceChartDataAB;
          break;
        case 14:
          dataInterfaceCurrent = interfaceChartDataPAB;
          break;
        case 15:
          dataInterfaceCurrent = interfaceChartDataE;
          break;
        case 16:
          dataInterfaceCurrent = interfaceChartDataD;
          break;
      }

      const topInterfaceValue1 = getMaxValue(dataInterfaceCurrent[interfaz], 'dslectura1');
      const topInterfaceValue2 = getMaxValue(dataInterfaceCurrent[interfaz], 'dslectura2');
      const minDslectura2 = Math.min(dataInterfaceCurrent[interfaz].map(item => item.dslectura2));
      const minUmbral = Math.min(dataInterfaceCurrent[interfaz].map(item => item.umbral));
      const bottomCalc = Math.min(minDslectura2, minUmbral) - 5;

      const addThreshold = Object.keys(dataInterfaceCurrent).reduce((acc, key) => {
        acc[key] = dataInterfaceCurrent[key].map((element) => ({
          ...element,
          'umbral': threshold !== null && threshold !== undefined ? String(threshold) : 0,
        }));
        return acc;
      }, {});

      setChartState({
        ...chartState,
        data: addThreshold[interfaz],
        top: topInterfaceValue1,
        top2: topInterfaceValue2,
        bottom2: bottomCalc
      });
    } else if (metric.id in nodeChartData) {
      const topValueNodeCategory = Math.max(...nodeChartData[metric.id].map((element) => Number(element.dslectura1))) + 1;
      const minDslectura2 = Math.min(...nodeChartData[metric.id].map(item => item.dslectura2));
      const minUmbral = Math.min(...nodeChartData[metric.id].map(item => item.umbral));
      const bottomCalc = Math.min(minDslectura2, minUmbral) - 5;

      const addThreshold = Object.keys(metricasGraphicData).reduce((acc, key) => {
        acc[key] = metricasGraphicData[key].map((element) => ({
          ...element,
          'umbral': threshold !== null && threshold !== undefined ? String(threshold) : 0,
        }));
        return acc;
      }, {});

      setChartState({
        ...chartState,
        data: addThreshold[metric.id],
        top: topValueNodeCategory,
        bottom2: bottomCalc
      });
    } else {
      setChartState({
        ...chartState,
        data: [],
      });
    }
    setLoading(false);
  };

  const changeMetric = (metricName) => {
    setInterfaz('')
    setMetric(metricName)
    const selectedThresHold = thresHoldsMetrics?.find(thresholdItem => 
      thresholdItem.id === metricName?.id
    ) || 0;
    if (selectedThresHold !== undefined) {
      setThreshold(selectedThresHold.umbralMax);
    }
  }

  const metricNameEntrada = (metricName) => {
    if (metricName?.id === 4) return 'Latencia ms';
    if (metricName?.id === 14) return 'Promedio de Ancho de Banda de Entrada';
    if (metricName?.id === 12) return 'Ancho Banda Entrada';
    if (metricName?.id === 15) return 'Errores Entrada';
    if (metricName?.id === 16) return 'Descartes Entrada';
    return metricName?.metrica;
  };

  const metricNameSalida = (metricName) => {
    if (metricName?.id === 14) return 'Promedio de Ancho de Banda de Salida';
    if (metricName?.id === 12) return 'Ancho Banda Salida';
    if (metricName?.id === 15) return 'Errores Salida';
    if (metricName?.id === 16) return 'Descartes Salida';
    return metricName?.metrica;
  };

  const handleDateInitChange = (newValue) => {
    if (newValue !== dateInit) {
      const dateIn = moment(newValue).format('Y-MM-DD HH:mm:00');
      setDateInit(dateIn);
      refreshChartData(nodo, dateIn, null);
    }
  };

  const handleDateEndChange = (newValue) => {
    if (newValue !== dateEnd) {
      const date = moment(newValue).format('Y-MM-DD HH:mm:00');
      setDateEnd(date);
      refreshChartData(nodo, null, date);
    }
  };

  return (
    <div
      className="flex max-w-full gap-3 flex-col justify-center align-middle"
      style={styleContainer}
    >
      <WrapperInputsGraficas enableMarginBottom>
        {nodes?.length > 0 && (
          <>
            <WrapperInput>
              <FormControl
                style={{
                  width: '95%',
                  marginBottom: '2rem',
                  marginTop: '1rem',
                }}
              >
                <InputLabel htmlFor="node-label">Selecciona el Nodo</InputLabel>
                <Select
                  labelId="node-select"
                  id="node-select-id"
                  label="node"
                  onChange={(e) => fetchChartData(e.target.value)}
                  defaultValue=""
                  value={nodo}
                  disabled={nodes?.length === 0}
                >
                  {nodes.map((nodeElement, index) => (
                    <MenuItem key={index} value={nodeElement?.id}>
                      {nodeElement?.nodo || 'Nodo'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </WrapperInput>
          </>
        )}
        {currentMetrics?.length > 0 && (
          <WrapperInput>
            <FormControl
              style={{
                width: '95%',
              }}
            >
              <InputLabel htmlFor="metrica-label">Selecciona la Métrica</InputLabel>
              <Select
                labelId="metrica-select"
                id="metrica-select-id"
                label="metrica"
                onChange={(e) => {
                  changeMetric(e.target.value);
                }}
                // defaultValue={metric}
                value={metric}
              >
                {currentMetrics.map((metricItem, index) => (
                  <MenuItem key={index} value={metricItem}>
                    {metricItem.metrica}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </WrapperInput>
        )}
        {!loading && currentMetrics?.length === 0 && <h3 style={{marginLeft: '6rem'}}>Sin datos para métricas</h3>}
        {graphicInterfaces?.length > 0 && [12, 14, 15, 16].includes(metric.id) && (
          <WrapperInput>
            <FormControl
              style={{
                width: '95%',
                left: '1rem',
              }}
            >
              <InputLabel htmlFor="interfaz-label">Selecciona la Interfaz</InputLabel>
              <Select
                labelId="interfaz-select"
                id="interfaz-select-id"
                label="interfaz"
                onChange={(e) => setInterfaz(e.target.value)}
                defaultValue=""
                value={interfaz || ''}
                disabled={graphicInterfaces?.length === 0}
              >
                {graphicInterfaces.map((equipmentItem, index) => (
                  <MenuItem key={index} value={equipmentItem.value}>
                    {equipmentItem.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </WrapperInput>
        )}
      </WrapperInputsGraficas>
      <div
        style={{
          display: 'flex',
          justifyContent: showDates ? 'space-between' : 'flex-end',
          alignItems: 'center',
          width: '100%',
          marginBottom: '1rem',
          marginTop: '1rem',
          marginRight: '1rem',
          marginLeft: '2rem',
        }}
      >
        <div>
          {showDates && (
            <Box sx={{width: '90%', marginRight: '1.4rem', marginLeft: '1rem', maxWidth: '180px'}}>
              <DatePicker
                onChange={(newValue) => handleDateInitChange(newValue)}
                label="Fecha Inicio"
                value={dateInit}
                renderInput={(params) => <TextField {...params} sx={{width: '95%'}}/>}
              />
            </Box>
          )}
        </div>
        <div>
          {showDates && (
            <Box sx={{width: '90%', marginLeft: '13rem', marginRight: '1.4rem', maxWidth: '180px',}}>
              <DatePicker
                onChange={(newValue) => handleDateEndChange(newValue)}
                label="Fecha Fin"
                value={dateEnd}
                maxDate={today}
                renderInput={(params) => <TextField {...params} sx={{width: '95%'}}/>}
              />
            </Box>
          )}
        </div>
        <div>
          <IconButton aria-label="line-chart-view" size="medium" onClick={() => setChartType('line')}>
            <TimelineIcon/>
          </IconButton>
        </div>
        <div>
          <IconButton aria-label="bar-chart-view" size="medium" onClick={() => setChartType('bar')}>
            <BarChartIcon/>
          </IconButton>
        </div>
        <div>
          <IconButton aria-label="refresh-chart" size="medium" className="refresh-icon"
                      onClick={() => refreshChartData(nodo)}>
            <AutorenewIcon/>
          </IconButton>
        </div>
        <div>
          <IconButton aria-label="download-chart" size="medium">
            <ExportCSVChart fileName={`${title}`} data={data}/>
          </IconButton>
        </div>
        <div>
          <IconButton aria-label="fullscreen" size="medium" onClick={handleOpenModal}>
            <FullscreenIcon/>
          </IconButton>
        </div>
      </div>
      {loading && <CircularProgress sx={{marginLeft: 10, paddingBottom: 10}}/>}
      {!loading && chartType === 'line' && (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 7,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis dataKey="fecha" type="category"/>
            <YAxis allowDataOverflow dataKey="dslectura1" domain={[bottom, top]} type="number" yAxisId="1"/>
            {metric && [12, 14, 15, 16].includes(metric.id) && (
              <YAxis orientation="right" allowDataOverflow dataKey="dslectura2" domain={[bottom, top2]} type="number"
                     yAxisId="1"/>
            )}
            {data?.length !== 0 && threshold !== null && (
              <YAxis
                orientation="right"
                allowDataOverflow
                dataKey="umbral"
                domain={[bottom, top2]}
                type="number"
                yAxisId="2"/>
            )}
            <Tooltip
              labelStyle={{color: '#000000'}}
              labelFormatter={(value) => {
                if (typeof value !== 'number') return `fecha: ${value}`;
                return `fecha: ${data[value]?.fecha}`;
              }}
              formatter={(value) => {
                if (metric?.id === 4) return `${Number(value).toFixed(4)} ms`;
                if (metric?.id !== 12 && metric?.id !== 14 && metric?.id !== 15 && metric?.id !== 16) return value;
                return `${Number(value).toFixed(4)} Mbps`;
              }}
            />
            {data?.length !== 0 && <Legend/>}
            <Line
              yAxisId="1"
              type="monotoneX"
              dataKey="dslectura1"
              stroke="#8884d8"
              animationDuration={300}
              name={metricNameEntrada(metric)}
              strokeWidth={3}
            />
            {metric && [12, 14, 15, 16].includes(metric.id) && (
              <Line
                yAxisId="1"
                type="monotoneX"
                dataKey="dslectura2"
                stroke="#47EDFD"
                animationDuration={300}
                name={metricNameSalida(metric)}
                strokeWidth={3}
              />
            )}
            {data?.length !== 0 && threshold !== null && (
              <Line
                type="monotoneX"
                dataKey="umbral"
                yAxisId="2"
                animationDuration={300}
                name="Umbral"
                dot={false}
              />
            )}
            <Brush dataKey="fecha" height={30} stroke="#8884d8"/>
          </LineChart>
        </ResponsiveContainer>
      )}
      {!loading && chartType === 'bar' && (<ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 7,
            }}
          >
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis allowDataOverflow dataKey="fecha" type="category"/>
            <YAxis allowDataOverflow dataKey="dslectura1" domain={[bottom, top]} type="number" yAxisId="1"/>
            {metric && [12, 14, 15, 16].includes(metric.id) && (
              <YAxis orientation="right" allowDataOverflow dataKey="dslectura2" type="number" yAxisId="1"/>
            )}
            {data?.length !== 0 && threshold !== null && (
              <YAxis
                orientation="right"
                allowDataOverflow
                dataKey="umbral"
                domain={[bottom, top2]}
                type="number"
                yAxisId="2"/>
            )}
            <Tooltip
              labelStyle={{color: '#000000'}}
              labelFormatter={(value) => {
                if (typeof value !== 'number') return `fecha: ${value}`;
                return `fecha: ${data[value]?.fecha}`;
              }}
            />
            {data?.length !== 0 && <Legend/>}
            <Bar yAxisId="1" type="monotoneX" dataKey="dslectura1" fill="#8884d8" animationDuration={300}
                 name={metric?.metrica}/>
            {metric && [12, 14, 15, 16].includes(metric.id) && (
              <Bar yAxisId="1" type="monotoneX" dataKey="dslectura2" fill="#47EDFD" animationDuration={300}
                   name={metric?.metrica}/>
            )}
            {data?.length !== 0 && threshold !== null && (
              <Line
                type="monotoneX"
                dataKey="umbral"
                yAxisId="2"
                animationDuration={300}
                name="Umbral"
                dot={false}
              />
            )}
            <Brush dataKey="fecha" height={30} stroke="#8884d8"/>
          </BarChart>
        </ResponsiveContainer>

      )}
      {/* Modal para el gráfico */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth fullScreen>
        <DialogTitle>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <strong className="title-chart">{title}</strong>
            <div>
              {showDates && (
                <Box sx={{width: '80%'}}>
                  <DatePicker
                    onChange={(newValue) => handleDateInitChange(newValue)}
                    label="Fecha Inicio"
                    value={dateInit}
                    renderInput={(params) => <TextField {...params} sx={{width: '95%'}}/>}
                  />
                </Box>
              )}
            </div>
            <div>
              {showDates && (
                <Box sx={{width: '80%'}}>
                  <DatePicker
                    onChange={(newValue) => handleDateEndChange(newValue)}
                    label="Fecha Fin"
                    value={dateEnd}
                    renderInput={(params) => <TextField {...params} sx={{width: '95%'}}/>}
                  />
                </Box>
              )}
            </div>
            <div>
              <IconButton aria-label="line-chart-view" size="medium" onClick={() => setChartType('line')}>
                <TimelineIcon/>
              </IconButton>
              <IconButton aria-label="bar-chart-view" size="medium" onClick={() => setChartType('bar')}>
                <BarChartIcon/>
              </IconButton>
              <IconButton aria-label="refresh-chart" size="medium" className="refresh-icon"
                          onClick={() => refreshChartData(nodo)}>
                <AutorenewIcon/>
              </IconButton>
              <IconButton aria-label="download-chart" size="medium">
                <ExportCSVChart fileName={`${title}`} data={data}/>
              </IconButton>
              <IconButton aria-label="fullscreen" size="medium" onClick={handleCloseModal}>
                <FullscreenExitIcon/>
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <div
            className="flex max-w-full gap-3 flex-col justify-center align-middle"
            style={{height: '95%', alignItems: 'center', width: '100%', marginTop: '2rem'}}
          >
            <WrapperInputsGraficas enableMarginBottom>
              {loading && <CircularProgress sx={{marginLeft: 10}}/>}
              {!loading && nodes?.length > 0 && (
                <>
                  <WrapperInput>
                    <FormControl
                      style={{
                        width: '100%',
                        marginLeft: '5rem',
                        marginBottom: '1rem',
                        marginTop: '1rem',
                      }}
                    >
                      <InputLabel htmlFor="node-label">Selecciona el Nodo</InputLabel>
                      <Select
                        labelId="node-select"
                        id="node-select-id"
                        label="node"
                        onChange={(e) => fetchChartData(e.target.value)}
                        defaultValue=""
                        value={nodo || ''}
                        disabled={nodes?.length === 0}
                      >
                        {nodes.map((nodeElement, index) => (
                          <MenuItem key={index} value={nodeElement?.id}>
                            {nodeElement?.nodo || 'Nodo'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </WrapperInput>
                </>
              )}
              {currentMetrics?.length > 0 && (
                <WrapperInput>
                  <FormControl
                    style={{
                      width: '100%',
                      left: '3rem',
                    }}
                  >
                    <InputLabel htmlFor="metrica-label">Selecciona la Métrica</InputLabel>
                    <Select
                      labelId="metrica-select"
                      id="metrica-select-id"
                      label="metrica"
                      onChange={(e) => {
                        setMetric(e.target.value);
                      }}
                      // defaultValue={metric}
                      value={metric || {}}
                    >
                      {currentMetrics.map((metricItem, index) => (
                        <MenuItem key={index} value={metricItem}>
                          {metricItem.metrica}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </WrapperInput>
              )}
              {!loading && currentMetrics?.length === 0 && <h3 style={{marginLeft: '6rem'}}>Sin datos para métricas</h3>}
              {!loading && graphicInterfaces?.length > 0 && [12, 14, 15, 16].includes(metric.id) && (
                <WrapperInput>
                  <FormControl
                    style={{
                      width: '100%',
                      marginLeft: '1rem',
                    }}
                  >
                    <InputLabel htmlFor="interfaz-label">Selecciona la Interfaz</InputLabel>
                    <Select
                      labelId="interfaz-select"
                      id="interfaz-select-id"
                      label="interfaz"
                      onChange={(e) => setInterfaz(e.target.value)}
                      defaultValue=""
                      value={interfaz || ''}
                      disabled={graphicInterfaces?.length === 0}
                    >
                      {graphicInterfaces.map((equipmentItem, index) => (
                        <MenuItem key={index} value={equipmentItem.value}>
                          {equipmentItem.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </WrapperInput>
              )}
            </WrapperInputsGraficas>
            {chartType === 'line' && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={800}
                  height={400}
                  data={data}
                  margin={{
                    top: 7,
                    // right: metric?.id === 12 ? 50 : 20,
                    left: 5,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3"/>
                  <XAxis dataKey="fecha" type="category"/>
                  <YAxis allowDataOverflow dataKey="dslectura1" domain={[bottom, top]} type="number" yAxisId="1"/>
                  {metric && [12, 14, 15, 16].includes(metric.id) && (
                    <YAxis orientation="right" allowDataOverflow dataKey="dslectura2" domain={[bottom, top2]}
                           type="number" yAxisId="1"/>
                  )}
                  {data?.length !== 0 && threshold !== null && (
                    <YAxis
                      orientation="right"
                      allowDataOverflow
                      dataKey="umbral"
                      domain={[bottom, top2]}
                      type="number"
                      yAxisId="2"/>
                  )}
                  <Tooltip
                    labelStyle={{color: '#000000'}}
                    labelFormatter={(value) => {
                      if (typeof value !== 'number') return `fecha: ${value}`;
                      return `fecha: ${data[value]?.fecha}`;
                    }}
                    formatter={(value) => {
                      if (metric?.id === 4) return `${Number(value).toFixed(4)} ms`;
                      if (metric?.id !== 12 && metric?.id !== 14 && metric?.id !== 15 && metric?.id !== 16) return value;
                      return `${Number(value).toFixed(4)} Mbps`;
                    }}
                  />
                  {data?.length !== 0 && <Legend/>}
                  <Line
                    yAxisId="1"
                    type="monotoneX"
                    dataKey="dslectura1"
                    stroke="#8884d8"
                    animationDuration={300}
                    name={metricNameEntrada(metric)}
                    strokeWidth={3}
                  />
                  {metric && [12, 14, 15, 16].includes(metric.id) && (
                    <Line
                      yAxisId="1"
                      type="monotoneX"
                      dataKey="dslectura2"
                      stroke="#47EDFD"
                      animationDuration={300}
                      name={metricNameSalida(metric)}
                      strokeWidth={3}
                    />
                  )}
                  {data?.length !== 0 && threshold !== null && (
                    <Line
                      type="monotoneX"
                      dataKey="umbral"
                      yAxisId="2"
                      animationDuration={300}
                      name="Umbral"
                      dot={false}
                    />
                  )}
                  <Brush dataKey="fecha" height={30} stroke="#8884d8"/>
                </LineChart>
              </ResponsiveContainer>
            )}

            {chartType === 'bar' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  width={800}
                  height={600}
                  data={data}
                  margin={{
                    top: 7,
                    left: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3"/>
                  <XAxis allowDataOverflow dataKey="fecha" type="category"/>
                  <YAxis allowDataOverflow dataKey="dslectura1" domain={[bottom, top]} type="number" yAxisId="1"/>
                  {metric && [12, 14, 15, 16].includes(metric.id) && (
                    <YAxis orientation="right" allowDataOverflow dataKey="dslectura2" type="number" yAxisId="1"/>
                  )}
                  {data?.length !== 0 && threshold !== null && (
                    <YAxis
                      orientation="right"
                      allowDataOverflow
                      dataKey="umbral"
                      domain={[bottom, top2]}
                      type="number"
                      yAxisId="2"/>
                  )}
                  <Tooltip
                    labelStyle={{color: '#000000'}}
                    labelFormatter={(value) => {
                      if (typeof value !== 'number') return `fecha: ${value}`;
                      return `fecha: ${data[value]?.fecha}`;
                    }}
                  />
                  {data?.length !== 0 && <Legend/>}
                  <Bar yAxisId="1" type="monotoneX" dataKey="dslectura1" fill="#8884d8" animationDuration={300}
                       name={metric?.metrica}/>
                  {metric && [12, 14, 15, 16].includes(metric.id) && (
                    <Bar yAxisId="1" type="monotoneX" dataKey="dslectura2" fill="#47EDFD" animationDuration={300}
                         name={metric?.metrica}/>
                  )}
                  {data?.length !== 0 && threshold !== null && (
                    <Line
                      type="monotoneX"
                      dataKey="umbral"
                      yAxisId="2"
                      animationDuration={300}
                      name="Umbral"
                      dot={false}
                    />
                  )}
                  <Brush dataKey="fecha" height={30} stroke="#8884d8"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetricChartItem;
